<app-auth-layout
  variant="split"
  [headline]="'user.authAside.createAccount.headline' | transloco"
  [tagline]="'user.authAside.createAccount.tagline' | transloco"
  [step]="currentStep"
  [stepCount]="2"
  [languages]="languageOptions"
  [currentLanguage]="language"
  (languageChange)="selectLanguage($event)"
>
  <form *ngIf="form" [formGroup]="form" (ngSubmit)="currentStep === 1 ? nextStep() : submit()">
    <ng-container *ngIf="currentStep === 1">
      <h2 class="auth-title">{{ 'user.createAccount.personalData' | transloco }}</h2>
      <p class="auth-subtitle">{{ 'user.createAccount.step1Subtitle' | transloco }}</p>

      <div class="field-group">
        <label class="field-label" for="country">{{ 'user.createAccount.country' | transloco }}*</label>
        <p-dropdown
          inputId="country"
          [options]="countryOptions"
          formControlName="country"
          (onChange)="setIsBrasil($event)"
          optionLabel="name"
          [filter]="true"
          filterBy="name"
          [showClear]="true"
          appendTo="body"
          panelStyleClass="auth-overlay"
          [placeholder]="'user.createAccount.country' | transloco"
          [emptyMessage]="'noResult' | transloco"
        />
      </div>

      <div class="field-group" *ngIf="isBrasil">
        <label class="field-label" for="docNumber">{{ 'user.createAccount.docNumber' | transloco }}*</label>
        <input
          class="text-input"
          id="docNumber"
          formControlName="docNumber"
          appDigitOnly
          [placeholder]="'common.placeholders.cpfCnpj' | transloco"
          (blur)="getMaskDocNumber()"
          maxlength="18"
        />
        <small *ngIf="messagesDocNumber.length" class="field-error">
          <i class="pi pi-exclamation-circle"></i> {{ messagesDocNumber[0].summary }}
        </small>
      </div>

      <div class="field-group">
        <label class="field-label" for="name">{{ 'user.createAccount.fullName' | transloco }}*</label>
        <input
          class="text-input"
          id="name"
          type="text"
          formControlName="name"
          [placeholder]="'user.createAccount.fullName' | transloco"
          maxlength="100"
        />
        <small *ngIf="messagesName.length" class="field-error">
          <i class="pi pi-exclamation-circle"></i> {{ messagesName[0].summary }}
        </small>
      </div>

      <div class="field-group" *ngIf="showBirthDate">
        <label class="field-label" for="birthDate">{{ 'user.createAccount.birthDate' | transloco }}*</label>
        <p-calendar
          *ngIf="languageCode === 'pt-br'"
          inputId="birthDate"
          formControlName="birthDate"
          appDateOnly
          [maxDate]="maxDate"
          dateFormat="dd/mm/yy"
          [showIcon]="true"
          [showOnFocus]="false"
          appendTo="body"
          panelStyleClass="auth-overlay"
          [placeholder]="'user.createAccount.birthDate' | transloco"
        />
        <p-calendar
          *ngIf="languageCode !== 'pt-br'"
          inputId="birthDate"
          formControlName="birthDate"
          appDateOnly
          [maxDate]="maxDate"
          dateFormat="mm/dd/yy"
          [showIcon]="true"
          [showOnFocus]="false"
          appendTo="body"
          panelStyleClass="auth-overlay"
          [placeholder]="'user.createAccount.birthDate' | transloco"
        />
      </div>

      <div class="field-group">
        <label class="field-label" for="phone">{{ 'user.createAccount.phone' | transloco }}*</label>
        <div class="phone-row">
          <p-dropdown
            class="phone-country"
            [options]="countries"
            formControlName="phoneCountry"
            optionLabel="name"
            (onChange)="updateMask()"
            [filter]="true"
            filterBy="name,dialCode"
            appendTo="body"
            panelStyleClass="auth-overlay auth-overlay-wide"
            [placeholder]="'user.createAccount.country' | transloco"
          >
            <ng-template let-country pTemplate="item">
              <div class="country-option">
                <img [src]="country.flag" [alt]="country.name" class="flag" />
                {{ country.name }} ({{ country.dialCode }})
              </div>
            </ng-template>
            <ng-template let-country pTemplate="selectedItem">
              <div class="country-option">
                <img [src]="country.flag" [alt]="country.name" class="flag" />
                {{ country.dialCode }}
              </div>
            </ng-template>
          </p-dropdown>
          <input
            class="text-input phone-number"
            id="phone"
            type="tel"
            formControlName="phone"
            [mask]="phoneMask"
            [validation]="false"
            [placeholder]="'user.createAccount.phone' | transloco"
          />
        </div>
      </div>

      <div class="auth-actions">
        <button class="chamfer" type="submit">{{ 'user.createAccount.continue' | transloco }}</button>
        <button class="chamfer outline" type="button" (click)="backLogin()">{{ 'user.createAccount.btnBack' | transloco }}</button>
      </div>
    </ng-container>

    <ng-container *ngIf="currentStep === 2">
      <h2 class="auth-title">{{ 'user.createAccount.step2Title' | transloco }}</h2>
      <p class="auth-subtitle">{{ 'user.createAccount.step2Subtitle' | transloco }}</p>

      <div class="field-group">
        <label class="field-label" for="email">{{ 'user.createAccount.email' | transloco }}*</label>
        <input
          class="text-input"
          id="email"
          type="email"
          formControlName="email"
          (blur)="checkEmail()"
          [placeholder]="'user.createAccount.email' | transloco"
        />
        <small *ngIf="messagesEmail.length" class="field-error">
          <i class="pi pi-exclamation-circle"></i> {{ messagesEmail[0].summary }}
        </small>
      </div>

      <div class="field-group">
        <label class="field-label" for="password">{{ 'user.createAccount.password' | transloco }}*</label>
        <div class="input-wrap">
          <input
            class="text-input"
            id="password"
            formControlName="password"
            autocomplete="new-password"
            [type]="showPassword ? 'text' : 'password'"
            (input)="equalsPassword()"
            [placeholder]="'user.createAccount.password' | transloco"
          />
          <button
            class="eye"
            type="button"
            (click)="showPassword = !showPassword"
            [attr.aria-pressed]="showPassword"
            [attr.aria-label]="(showPassword ? 'user.login.hidePassword' : 'user.login.showPassword') | transloco"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z"/><circle cx="12" cy="12" r="2.5"/></svg>
          </button>
        </div>
        <app-password-strength-checklist [password]="form.get('password')?.value"></app-password-strength-checklist>
        <small class="help-text" id="password-help">{{ 'user.createAccount.passwordText' | transloco }}</small>
      </div>

      <div class="field-group">
        <label class="field-label" for="passwordConfirm">{{ 'user.createAccount.passwordConfirm' | transloco }}*</label>
        <div class="input-wrap">
          <input
            class="text-input"
            [class.match]="passwordsMatch"
            id="passwordConfirm"
            formControlName="passwordConfirm"
            autocomplete="new-password"
            [type]="showPasswordConfirm ? 'text' : 'password'"
            (input)="equalsPassword()"
            [placeholder]="'user.createAccount.passwordConfirm' | transloco"
          />
          <button
            class="eye"
            type="button"
            (click)="showPasswordConfirm = !showPasswordConfirm"
            [attr.aria-pressed]="showPasswordConfirm"
            [attr.aria-label]="(showPasswordConfirm ? 'user.login.hidePassword' : 'user.login.showPassword') | transloco"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z"/><circle cx="12" cy="12" r="2.5"/></svg>
          </button>
        </div>
        <small *ngIf="messagesPassword.length" class="field-error">
          <i class="pi pi-exclamation-circle"></i> {{ messagesPassword[0].summary }}
        </small>
      </div>

      <p-toast key="toastCreateAccount" />

      <div class="field-group">
        <label class="check">
          <input type="checkbox" id="checkCookies" formControlName="checkCookies" (change)="changeCookies()" />
          <span>
            {{ 'user.createAccount.checkCookies.v1' | transloco }}
            <a [href]="termsofusepdf" target="_blank">{{ 'user.createAccount.checkCookies.v2' | transloco }}</a>
            {{ 'user.createAccount.checkCookies.v3' | transloco }}
            <a [href]="privacypolicypdf" target="_blank">{{ 'user.createAccount.checkCookies.v4' | transloco }}</a>
          </span>
        </label>
        <small *ngIf="messagesCookies.length" class="field-error">
          <i class="pi pi-exclamation-circle"></i> {{ messagesCookies[0].summary }}
        </small>
      </div>

      <div class="field-group">
        <label class="check">
          <input type="checkbox" id="checkNewsletter" formControlName="checkNewsletter" />
          <span>{{ 'user.createAccount.checkNewsletter' | transloco }}</span>
        </label>
      </div>

      <div class="auth-actions">
        <button class="chamfer" type="submit" [disabled]="submitBtnLoading">
          <i *ngIf="submitBtnLoading" class="pi pi-spin pi-spinner"></i>
          {{ 'user.createAccount.btnCreateAccount' | transloco }}
        </button>
        <button class="chamfer outline" type="button" (click)="previousStep()">{{ 'user.createAccount.btnBack' | transloco }}</button>
      </div>
    </ng-container>
  </form>
</app-auth-layout>

<p-toast key="toastMessages" />
